<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import { useI18n } from 'vue-i18n'
import SectionHeading from '@/components/SectionHeading.vue'
import OverviewTable from '@/components/estate-objects/OverviewTable.vue'
import ProjectSuggestionDetailData = App.Data.Bank.ProjectSuggestionDetailData
import { computed, inject } from 'vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import { isProjectSensitiveKey } from '@/utils/sensitive-fields'
import { countryName } from '@/utils/country'
import ExistingEstateSummaryChart from '@/components/ExistingEstateSummaryChart.vue'
import CompanyOrganizationChart from '@/components/companies/CompanyOrganizationChart.vue'

const props = defineProps<{
  project: ProjectSuggestionDetailData
}>()

const { t, te, locale } = useI18n()

function professionLabel(profession?: string | null): string {
  if (!profession) {
    return ''
  }

  return te(`enums.professionType.${profession}`) ? t(`enums.professionType.${profession}`) : profession
}

const isProjectSensitive = inject(
  isProjectSensitiveKey,
  computed(() => false),
)

const person = computed(() => [
  {
    label: t('gender_label'),
    value: t(`gender.${props.project.investor.gender}`),
  },
  {
    label: t('last_name'),
    value: props.project.investor.last_name,
    sensitiveField: 'NaturalPersonData.last_name',
  },
  {
    label: t('first_name'),
    value: props.project.investor.first_name,
    sensitiveField: 'NaturalPersonData.first_name',
  },
  {
    label: t('address'),
    value: props.project.investor.address,
    sensitiveField: 'NaturalPersonData.address',
  },
  {
    label: t('birth_date'),
    value: props.project.investor.birth_date,
    format: 'date' as const,
  },
  {
    label: t('birth_place'),
    value: props.project.investor.birth_place,
  },
  {
    label: t('profession'),
    value: professionLabel(props.project.investor.profession),
  },
  {
    label: t('marital_status'),
    value: t(`enums.maritalStatus.${props.project.investor.marital_status}`),
  },
  {
    label: t('nationality'),
    value: countryName(props.project.investor.nationality, locale.value),
  },
  {
    label: t('has_children'),
    value: props.project.investor.has_children,
    format: 'boolean' as const,
  },
])

const companyInformation = computed(() => {
  const company = props.project.company

  if (!company) {
    return []
  }

  return [
    {
      label: t('company.name'),
      value: company.name,
      sensitiveField: 'CompanyData.name',
    },
    {
      label: t('company.managing_director'),
      value: company.managing_director,
      sensitiveField: 'CompanyData.managing_director',
    },
    {
      label: t('company.address'),
      value: company.address,
      sensitiveField: 'CompanyData.address',
    },
    {
      label: t('company.commercial_registration_number'),
      value: company.commercial_registration_number,
    },
    {
      label: t('company.registration_court'),
      value: company.registration_court,
    },
    {
      label: t('company.date_of_establishment'),
      value: company.date_of_establishment,
      format: 'date' as const,
    },
    {
      label: t('company.email'),
      value: company.email,
    },
    {
      label: t('company.website'),
      value: company.website,
    },
    {
      label: t('company.telephone'),
      value: company.telephone,
    },
  ]
})

const existingEstateObjects = computed(() => [
  ...props.project.investor_estate_objects,
  ...props.project.company_estate_objects,
])

function companyShortInfo(company: NonNullable<ProjectSuggestionDetailData['company']>): string {
  const establishmentYear = company.date_of_establishment ? new Date(company.date_of_establishment).getFullYear() : null

  return [
    t('short_info.company.type'),
    establishmentYear ? t('short_info.company.established', { year: establishmentYear }) : null,
  ]
    .filter(Boolean)
    .join(' ')
}

function investorShortInfo(investor: ProjectSuggestionDetailData['investor']): string {
  const age = calculateAge(investor.birth_date)

  return [
    t('short_info.person.type'),
    age !== null ? t('short_info.person.age', { age }) : null,
    investor.marital_status
      ? t('short_info.person.marital_status', { status: t(`enums.maritalStatus.${investor.marital_status}`) })
      : null,
    investor.profession
      ? t('short_info.person.profession', { profession: professionLabel(investor.profession) })
      : null,
    investor.has_children != null
      ? t(investor.has_children ? 'short_info.person.has_children' : 'short_info.person.has_no_children')
      : null,
  ]
    .filter(Boolean)
    .join(' ')
}

const shortInfoText = computed(() =>
  props.project.company ? companyShortInfo(props.project.company) : investorShortInfo(props.project.investor),
)

function calculateAge(birthDate?: string | null): number | null {
  if (!birthDate) return null

  const birth = new Date(birthDate)
  const today = new Date()
  const age = today.getFullYear() - birth.getFullYear()
  const monthDay = (d: Date) => d.getMonth() * 100 + d.getDate()

  return age - (monthDay(today) < monthDay(birth) ? 1 : 0)
}
</script>
<template>
  <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
    <FinestCard :title="t('short_info_about_borrower')" icon="pi pi-list">
      {{ shortInfoText }}
    </FinestCard>
    <FinestCard
      :title="t('person')"
      icon="pi pi-user"
      :locked="isProjectSensitive"
      :locked-text="t('apply_to_see_more')">
      <div class="mt-2 grid gap-x-12 gap-y-8 md:grid-cols-1 xl:grid-cols-2">
        <ValueDisplay
          v-for="item in person"
          :key="item.label"
          :label="item.label"
          :value="item.value"
          :sensitive-field="item.sensitiveField"
          :format="item.format ?? 'string'"
          css="flex items-center gap-3 font-semibold text-surface-950" />
      </div>
    </FinestCard>
    <FinestCard :title="t('existing_estate_summary')" icon="pi pi-chart-pie">
      <ExistingEstateSummaryChart :estate-objects="existingEstateObjects" />
    </FinestCard>
  </div>
  <div>
    <div v-if="project.company">
      <FinestCard
        class="col-span-2"
        :title="t('company_information')"
        icon="pi pi-building"
        :locked="isProjectSensitive"
        :locked-text="t('apply_to_see_more')">
        <div v-if="companyInformation.length" class="grid gap-x-12 gap-y-8 md:grid-cols-2 xl:grid-cols-3">
          <ValueDisplay
            v-for="item in companyInformation"
            :key="item.label"
            :label="item.label"
            :value="item.value"
            :sensitive-field="item.sensitiveField"
            :format="item.format ?? 'string'"
            css="flex items-center gap-3 font-semibold text-surface-950" />
        </div>
        <CompanyOrganizationChart
          v-if="project.company_organization"
          :organization="project.company_organization"
          :interactive="false"
          class="mt-8" />
        <p v-if="!companyInformation.length" class="text-surface-500 text-sm">
          {{ t('company.no_company') }}
        </p>
      </FinestCard>
    </div>
    <div class="mt-6">
      <FinestCard
        :title="t('existing_estate_objects')"
        icon="pi pi-list"
        :locked="isProjectSensitive"
        :locked-text="t('apply_to_see_more')">
        <OverviewTable
          v-if="project.investor_estate_objects.length"
          :estate-objects="project.investor_estate_objects"
          :print="true"
          show-bank-loan-metrics />
        <SectionHeading class="mt-10">
          {{ t('company_portfolio') }}
        </SectionHeading>
        <OverviewTable
          v-if="project.company_estate_objects.length"
          :estate-objects="project.company_estate_objects"
          :print="true"
          show-bank-loan-metrics />
      </FinestCard>
    </div>
  </div>
</template>

<i18n>
{
    "de": {
        "balance_sheets": "Bilanzen",
        "short_info_about_borrower": "Kurze Info zum Kreditnehmer",
        "company_information": "Gesellschaftsinformation",
        "existing_estate_summary": "Bestandsimmobilien Zusammenfassung",
        "existing_estate_objects": "Bestandsimmobilien",
        "company_portfolio": "Gesellschaftsportfolio",
        "person": "Zur Person",
        "apply_to_see_more": "Stelle einen Antrag, um mehr zu sehen",
        "gender_label": "Geschlecht",
        "gender": {
          "male": "Männlich",
          "female": "Weiblich",
          "diverse": "Divers"
        },
        "last_name": "Nachname",
        "first_name": "Vorname",
        "address": "Anschrift",
        "birth_date": "Geburtsdatum",
        "birth_place": "Geburtsort",
        "profession": "Beruf",
        "marital_status": "Familienstand",
        "nationality": "Nationalität",
        "has_children": "Kinder",
        "short_info": {
          "person": {
            "type": "Der Kreditnehmer ist eine natürliche Person.",
            "age": "Die Person ist {age} Jahre alt.",
            "marital_status": "Der Familienstand ist {status}.",
            "profession": "Die Person ist als {profession} tätig.",
            "has_children": "Es sind Kinder vorhanden.",
            "has_no_children": "Es sind keine Kinder vorhanden."
          },
          "company": {
            "type": "Der Kreditnehmer ist eine Gesellschaft.",
            "established": "Die Gesellschaft wurde im Jahr {year} gegründet."
          }
        },
        "company": {
          "name": "Firmenname",
          "managing_director": "Geschäftsführer",
          "address": "Adresse",
          "commercial_registration_number": "Handelsregisternummer",
          "registration_court": "Registergericht",
          "date_of_establishment": "Gründungsdatum",
          "email": "E-Mail",
          "website": "Webseite",
          "telephone": "Telefon",
          "no_company": "Keine Gesellschaft vorhanden"
        },
      "no_balance_sheets_available": "Keine Bilanzen verfügbar"
    },
    "en": {
        "balance_sheets": "Balance Sheets",
        "short_info_about_borrower": "Short info about borrower",
        "company_information": "Company information",
        "existing_estate_summary": "Existing Properties Summary",
        "existing_estate_objects": "Existing Properties",
        "company_portfolio": "Company Portfolio",
        "person": "About the person",
        "apply_to_see_more": "Submit a request to see more",
        "gender_label": "Gender",
        "gender": {
          "male": "Male",
          "female": "Female",
          "diverse": "Diverse"
        },
        "last_name": "Last Name",
        "first_name": "First Name",
        "address": "Address",
        "birth_date": "Date of Birth",
        "birth_place": "Birth Place",
        "profession": "Profession",
        "marital_status": "Marital Status",
        "nationality": "Nationality",
        "has_children": "Children",
        "short_info": {
          "person": {
            "type": "The borrower is a natural person.",
            "age": "The person is {age} years old.",
            "marital_status": "The marital status is {status}.",
            "profession": "The person works as {profession}.",
            "has_children": "The person has children.",
            "has_no_children": "The person has no children."
          },
          "company": {
            "type": "The borrower is a company.",
            "established": "The company was established in {year}."
          }
        },
        "company": {
          "name": "Company name",
          "managing_director": "Managing director",
          "address": "Address",
          "commercial_registration_number": "Commercial register number",
          "registration_court": "Registration court",
          "date_of_establishment": "Date of establishment",
          "email": "Email",
          "website": "Website",
          "telephone": "Telephone",
          "no_company": "No company available"
        },
      "no_balance_sheets_available": "No balance sheets available"
    }
}
</i18n>
